import { useCallback, useEffect, useRef, useState } from 'react'

export default function useModalAccessibility({ onClose, initialFocusSelector = '[data-modal-close]' }) {
  const [isVisible, setIsVisible] = useState(false)
  const dialogRef = useRef(null)
  const previousActiveElement = useRef(null)

  useEffect(() => {
    previousActiveElement.current = document.activeElement
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = useCallback(() => {
    setIsVisible(false)
    setTimeout(() => {
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
      onClose()
    }, 300)
  }, [onClose])

  // Focus management and keyboard handling
  useEffect(() => {
    if (!isVisible || !dialogRef.current) return

    const target = dialogRef.current.querySelector(initialFocusSelector)
    if (target) target.focus()

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose()
        return
      }

      if (e.key === 'Tab') {
        const focusable = dialogRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isVisible, handleClose, initialFocusSelector])

  return { dialogRef, isVisible, handleClose }
}
